import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { USER_ROLE } from "../../../constants/app";
import { ERROR_MESSAGE } from "../../../constants/errorMessage";
import { PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import { authSelectors } from "../../../ducks/auth";
import { clientActions, clientSelectors } from "../../../ducks/client";
import { groupActions, groupSelectors } from "../../../ducks/group";
import { systemActions, systemSelectors } from "../../../ducks/system";
import { User, UserRole, userActions } from "../../../ducks/user";
import { useNavigation } from "../../../hooks/useNavigation";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import { validateEmail, validatePhoneNumber } from "../../../utils/validation";

export const useUserEdit = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();
  const { userId } = useParams<{ userId: string }>();

  const loggedInUser = useSelector(authSelectors.loggedInUser);
  const clients = useSelector(clientSelectors.clients);
  const groups = useSelector(groupSelectors.groups);
  const systems = useSelector(systemSelectors.systems);

  const [user, setUser] = useState<User | null>(null);

  const canEditGroup = useMemo(() => {
    if (!loggedInUser) return false;

    return loggedInUser.role === UserRole.Admin;
  }, [loggedInUser]);

  useEffect(() => {
    dispatch(systemActions.getSystemList.started({}));
  }, []);

  useEffect(() => {
    if (user || !userId || !loggedInUser) return;

    if (loggedInUser.role === UserRole.Admin) {
      dispatch(appActions.showLoading());

      dispatch(
        clientActions.fetchClients.started({
          sort_key: "id",
          sort_order: "asc",
          onError: () => dispatch(appActions.hideLoading()),
          onSuccess: fetchUserInfo,
        }),
      );
    } else {
      fetchUserInfo();
    }
  }, [loggedInUser]);

  const fetchUserInfo = useCallback(() => {
    if (!userId) return;

    dispatch(
      groupActions.fetchGroups.started({
        sort_key: "id",
        sort_order: "asc",
        onError: () => dispatch(appActions.hideLoading()),
        onSuccess: () => {
          dispatch(
            userActions.getUserDetails.started({
              accountId: userId,
              onComplete: () => dispatch(appActions.hideLoading()),
              onSuccess: (data) => {
                const userData = data.data;
                const user: User = {
                  id: userData.id,
                  name: `${userData.last_name} ${userData.first_name}`,
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  email: userData.email,
                  phoneNumber: userData.phone || undefined,
                  clientName: userData.customer_name,
                  groupName: userData.group_name,
                  role:
                    userData.role === USER_ROLE.SYSTEM_ADMINISTRATOR
                      ? UserRole.Admin
                      : userData.role === USER_ROLE.USER
                        ? UserRole.User
                        : UserRole.GroupAdmin,
                  systemPermissions: userData.system_permission,
                  validPeriod: {
                    from: new Date(userData.valid_from),
                    to: new Date(userData.valid_to),
                  },
                  lastLogin: userData.last_login
                    ? new Date(userData.last_login)
                    : null,
                  createdAt: new Date(userData.created_at),
                  createdBy: userData.created_by,
                };

                setUser(user);
              },
              onError: () => {
                navigateToPage(PageRoutes.USERS);
              },
            }),
          );
        },
      }),
    );
  }, [userId]);

  const handleCancel = useCallback(() => {
    dispatch(
      appActions.showModal({
        title: "内容は保存されません。よろしいですか？",
        buttons: [
          {
            variant: "dark",
            text: "閉じる",
            onClick: () => {
              dispatch(appActions.hideModal());
            },
          },
          {
            variant: "filled",
            text: "保存しない",
            onClick: () => {
              dispatch(appActions.hideModal());
              navigateToPage(PageRoutes.USERS);
            },
          },
        ],
      }),
    );
  }, []);

  const handleEditUser = useCallback(
    (
      firstName: string,
      lastName: string,
      email: string,
      phoneNumber: string,
      clientId: string,
      groupId: string,
      role: string,
      validFrom: string,
      validTo: string,
      systemPermissions: string[],
      onError: (message: string) => void,
    ) => {
      const isValidEmail = validateEmail(email);
      const isValidPhoneNumber = phoneNumber
        ? validatePhoneNumber(phoneNumber)
        : true;

      if (!isValidEmail) return onError(ERROR_MESSAGE.invalidEmail);

      if (phoneNumber && !isValidPhoneNumber)
        return onError(ERROR_MESSAGE.invalidPhoneNumber);

      if (!userId) return;

      dispatch(appActions.showLoading());
      dispatch(
        userActions.updateUser.started({
          accountId: userId,
          first_name: firstName,
          last_name: lastName,
          email,
          phone: phoneNumber || undefined,
          // company_id: clientId,
          group_id: groupId,
          role,
          valid_from: formatDateTime(validFrom, false, "YYYY-MM-DDThh:mm:ssZ"),
          valid_to: formatDateTime(validTo, false, "YYYY-MM-DDThh:mm:ssZ"),
          system_permission: systemPermissions,
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: () => {
            dispatch(
              appActions.showNotification({
                type: "success",
                message: "アカウントを更新しました",
              }),
            );
            navigateToPage(PageRoutes.USERS);
          },
        }),
      );
    },
    [userId],
  );

  return {
    canEditGroup,
    user,
    clients,
    groups,
    systems,
    handleCancel,
    handleEditUser,
  };
};
